import { useState, useEffect, useCallback } from 'react';
import { useServices, useToast } from '@/app/providers';
import type { Journal, JournalParams, ExportFormat } from '@yawi/types';
import { downloadBlob, generateReportFilename } from '../utils/download';

function currentMonth(): string {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

export const useJournal = () => {
  const { reportRepo } = useServices();
  const toast = useToast();

  const [report, setReport] = useState<Journal | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [params, setParams] = useState<JournalParams>({ month: currentMonth() });

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      setReport(await reportRepo.getJournal(params));
    } catch (e) {
      console.error('Error loading journal:', e);
      toast.error('Error al cargar el libro diario');
    } finally {
      setLoading(false);
    }
  }, [reportRepo, params, toast]);

  const exportReport = useCallback(
    async (format: ExportFormat) => {
      setExporting(true);
      try {
        const blob = await reportRepo.exportJournal(format, params);
        downloadBlob(blob, generateReportFilename(`libro-diario-${params.month}`, format));
        toast.success('Reporte exportado correctamente');
      } catch (e) {
        console.error('Error exporting journal:', e);
        toast.error('Error al exportar el reporte');
      } finally {
        setExporting(false);
      }
    },
    [reportRepo, params, toast],
  );

  const setMonth = useCallback((month: string) => setParams((p) => ({ ...p, month })), []);
  const setOpeningBalance = useCallback(
    (openingBalance: number | undefined) => setParams((p) => ({ ...p, openingBalance })),
    [],
  );

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  return { report, loading, exporting, params, setMonth, setOpeningBalance, refetch: fetchReport, exportReport };
};
